import type { Href } from "expo-router";

import type { AppRole, PortalKey } from "./auth";
import { isAdminRole, roleToRoute } from "./auth";
import { getAAL, hasVerifiedTotp, listFactors } from "./mfa";

type ReplaceRouter = { replace: (href: Href) => void };

/**
 * Settings page where a user can manage (or re-enroll) their MFA factor.
 *
 * For admins this is the dedicated /admin/mfa/enroll screen - admin MFA is
 * mandatory and has its own onboarding-style flow. For everyone else the
 * Security toggle lives inside the portal's Settings page.
 */
function mfaEnrollHrefForRole(role: AppRole): Href {
  if (isAdminRole(role)) return "/admin/mfa/enroll" as Href;
  const portal = roleToRoute(role);
  return `${portal}/settings` as Href;
}

/**
 * After password auth, send the user through TOTP enrollment or step-up
 * challenge when their account requires MFA. Works for any role: admins are
 * required by default, non-admins are opt-in via Settings -> Security.
 * @returns `true` if navigation was handled (caller should stop).
 */
export async function redirectIfMfaIncomplete(
  router: ReplaceRouter,
  input: { role: AppRole; mfaRequired: boolean }
): Promise<boolean> {
  const { role, mfaRequired } = input;
  if (!mfaRequired) return false;

  try {
    const factors = await listFactors();
    if (!hasVerifiedTotp(factors)) {
      // Required but no factor -> send to enrollment.
      router.replace(mfaEnrollHrefForRole(role));
      return true;
    }
    const aal = await getAAL();
    if (aal?.nextLevel === "aal2" && aal?.currentLevel === "aal1") {
      router.replace("/login/mfa-challenge" as Href);
      return true;
    }
  } catch {
    router.replace(mfaEnrollHrefForRole(role));
    return true;
  }
  return false;
}

/** Back-compat alias - older callers used the admin-specific name. */
export const redirectAdminIfMfaIncomplete = redirectIfMfaIncomplete;

/**
 * PortalGate / session guard state for the current user. `enroll` = required
 * but no factor; `challenge` = factor exists but session is still aal1; `ok` =
 * either MFA isn't required for this user or they've already stepped up.
 */
export async function getMfaPortalState(input: {
  role: AppRole | null;
  mfaRequired: boolean;
}): Promise<"enroll" | "challenge" | "ok"> {
  if (!input.role || !input.mfaRequired) return "ok";
  try {
    const factors = await listFactors();
    if (!hasVerifiedTotp(factors)) return "enroll";
    const aal = await getAAL();
    if (aal?.nextLevel === "aal2" && aal?.currentLevel === "aal1") return "challenge";
    return "ok";
  } catch {
    return "enroll";
  }
}

/** Back-compat alias - older callers used the admin-specific name. */
export const getAdminMfaPortalState = getMfaPortalState;

/** Where to land a non-admin who's in `enroll` state for a given portal.
 *  Every PortalKey variant must be handled — the function's return type
 *  is `Href`, so omitting a branch silently returns `undefined` at
 *  runtime and crashes the navigation. */
export function nonAdminMfaEnrollHref(portal: PortalKey): Href {
  switch (portal) {
    case "student":
      return "/student/settings" as Href;
    case "client":
      return "/client/settings" as Href;
    case "judge":
      return "/judge/settings" as Href;
    case "campus":
      return "/campus/settings" as Href;
    case "admin":
      return "/admin/mfa/enroll" as Href;
  }
}
